import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { RichText } from "../components/site/RichText";
import { LeadDialog } from "../components/site/LeadDialog";
import { WhatsAppFloat } from "../components/site/WhatsAppFloat";
import { formatDate } from "../components/site/PostCard";
import { Seo } from "../components/Seo";
import { sharePostUrl, WhatsAppIcon } from "../components/site/WhatsAppIcon";
import { Button } from "../components/ui/button";
import { fetchPost } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    fetchPost(slug)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const readingTime = post
    ? Math.max(1, Math.round(post.content.split(/\s+/).length / 200))
    : 0;

  return (
    <div className="min-h-screen bg-paper" data-testid="blog-post-page">
      <Header />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-32 sm:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-forest hover:text-gold-dark transition-colors duration-150"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        {!post && !notFound && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        )}

        {notFound && (
          <div className="py-20 text-center" data-testid="post-not-found">
            <h1 className="font-display text-3xl text-forest">Article not found</h1>
            <p className="mt-3 text-sm text-[#4A4C4A]">It may have been moved or unpublished.</p>
          </div>
        )}

        {post && (
          <article>
            <Seo
              title={`${post.title} | BIDC Journal`}
              description={post.excerpt || post.title}
              path={`/blog/${post.slug}`}
              image={post.cover}
              type="article"
            >
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: post.title,
                  description: post.excerpt || post.title,
                  image: post.cover ? [window.location.origin + post.cover] : undefined,
                  datePublished: post.created_at,
                  dateModified: post.updated_at,
                  author: { "@type": "Organization", name: "Tirupati Developers — BIDC" },
                  publisher: { "@type": "Organization", name: "Tirupati Developers — BIDC" },
                  mainEntityOfPage: {
                    "@type": "WebPage",
                    "@id": `${window.location.origin}/blog/${post.slug}`,
                  },
                })}
              </script>
            </Seo>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] font-medium text-gold-dark">
                {formatDate(post.created_at)} · {readingTime} min read
                {post.category && post.category !== "General" ? ` · ${post.category}` : ""}
              </p>
              <a
                href={sharePostUrl(post)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="post-share-whatsapp"
                className="inline-flex items-center gap-2 border border-[#25D366]/50 bg-[#25D366]/10 px-3.5 py-1.5 text-xs font-medium text-[#128C4B] transition-colors duration-150 hover:bg-[#25D366]/20"
              >
                <WhatsAppIcon className="h-4 w-4" /> Share on WhatsApp
              </a>
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium leading-[1.12] tracking-tight text-forest" data-testid="post-title">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-5 text-lg leading-relaxed text-[#4A4C4A] italic">{post.excerpt}</p>
            )}
            {post.cover && (
              <img
                src={post.cover}
                alt={post.title}
                className="mt-8 w-full border border-border object-cover max-h-[420px]"
                loading="lazy"
              />
            )}
            <RichText text={post.content} className="mt-10" />

            <div className="mt-14 border border-gold/50 bg-gold-pale/40 p-7 text-center">
              <p className="font-display text-2xl font-medium text-forest">
                See the estate for yourself
              </p>
              <p className="mt-2 text-sm text-[#4A4C4A]">
                Plots, sheds and approvals — walk through them all with our team.
              </p>
              <LeadDialog type="site_visit">
                <Button
                  className="mt-5 bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
                  data-testid="post-cta-button"
                >
                  Book a Site Visit
                </Button>
              </LeadDialog>
              <div className="mt-3">
                <a
                  href={sharePostUrl(post)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-[#128C4B] hover:text-forest transition-colors duration-150"
                >
                  Share this article on WhatsApp →
                </a>
              </div>
            </div>
          </article>
        )}
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
